import { toast } from 'sonner';

export interface ActionFeedback {
  level: 'success' | 'info' | 'error' | 'warning';
  key?: string;
  text?: string;
  params?: Record<string, string | number>;
}

export type StatusAction = 'accept' | 'reject' | 'hire';

const ALREADY_IN_STAGE = 'candidate is already in this stage';
const INTERVIEWED_TO_INTERVIEWING =
  'cannot move a candidate from interviewed back to interviewing';

const INVITE_SENT_STAGES = new Set(['interviewing', 'interviewed']);

const STATUS_FEEDBACK: Record<
  StatusAction,
  { success: string; skipped: string; failed: string }
> = {
  accept: {
    success: 'accepted_success',
    skipped: 'acceptance_already_sent',
    failed: 'accept_failed',
  },
  reject: {
    success: 'rejected_success',
    skipped: 'rejection_already_sent',
    failed: 'reject_failed',
  },
  hire: {
    success: 'hired_success',
    skipped: 'already_in_stage',
    failed: 'hire_failed',
  },
};

const STATUS_DONE_STAGE: Record<StatusAction, string> = {
  accept: 'accepted',
  reject: 'rejected',
  hire: 'hired',
};

/** Side-effect toasts when pipeline-moves reports actions_queued. */
const MOVE_SIDE_EFFECT: Record<string, { queued: string; skipped: string }> = {
  interviewing: {
    queued: 'interview_sent',
    skipped: 'interview_already_sent',
  },
  accepted: {
    queued: 'recruiter_interview_invitation_sent',
    skipped: 'acceptance_already_sent',
  },
  rejected: {
    queued: STATUS_FEEDBACK.reject.success,
    skipped: STATUS_FEEDBACK.reject.skipped,
  },
};

function notSentKeyForStage(toStageType?: string): string {
  if (toStageType === 'interviewing') {
    return 'ai_interview_invitation_not_sent';
  }
  if (toStageType === 'accepted') {
    return 'recruiter_interview_invitation_not_sent';
  }
  return 'notification_not_sent';
}

export function isAlreadyInStageError(message: string): boolean {
  return message.toLowerCase().includes(ALREADY_IN_STAGE);
}

function isInterviewedToInterviewingError(message: string): boolean {
  return message.toLowerCase().includes(INTERVIEWED_TO_INTERVIEWING);
}

function isOpaqueError(message: string): boolean {
  const trimmed = message.trim();
  if (!trimmed || trimmed.toLowerCase() === 'failed') return true;
  return /^error:\s*\d+/i.test(trimmed);
}

export function feedbackErrorMessage(error: unknown): string {
  return error instanceof Error ? error.message : String(error);
}

function feedbackFromError(
  message: string,
  fallbackKey: string,
  alreadyDoneKey = 'already_in_stage'
): ActionFeedback {
  if (isAlreadyInStageError(message)) {
    return { level: 'info', key: alreadyDoneKey };
  }
  if (isInterviewedToInterviewingError(message)) {
    return { level: 'error', key: 'interviewed_to_interviewing' };
  }
  if (isOpaqueError(message)) {
    return { level: 'error', key: fallbackKey };
  }
  return { level: 'error', text: message.trim() };
}

/**
 * Whether this candidate already has an interview invitation.
 *
 * Deliberately does NOT look at `interviewInvitationToken`: that maps to
 * `candidates.invitation_token`, which the backend generates when the
 * candidate row is created, not when an invitation is sent. Treating it as
 * "sent" made every candidate look invited, so Send Interview silently
 * skipped and toasted "already sent" instead of mailing anyone.
 *
 * The real signals are an interview row (`interviewId`) and the stages a
 * candidate only reaches once an invitation went out.
 */
export function isInterviewInvitationAlreadySent(candidate: {
  pipelineStageSemantic?: string;
  interviewId?: string;
}): boolean {
  if (candidate.interviewId) {
    return true;
  }
  return INVITE_SENT_STAGES.has(candidate.pipelineStageSemantic ?? '');
}

export function isStatusActionAlreadyApplied(
  action: StatusAction,
  stageSemantic?: string
): boolean {
  return stageSemantic === STATUS_DONE_STAGE[action];
}

export function feedbackForStatusChange(input: {
  action: StatusAction;
  error?: string;
  skipped?: boolean;
}): ActionFeedback {
  const keys = STATUS_FEEDBACK[input.action];
  if (input.error) {
    return feedbackFromError(input.error, keys.failed, keys.skipped);
  }
  if (input.skipped) {
    return { level: 'info', key: keys.skipped };
  }
  return { level: 'success', key: keys.success };
}

export function feedbackForSendInterview(input: {
  httpError?: string;
  skipped?: boolean;
  /** When false after a successful select, invitation email was not queued. */
  willNotify?: boolean;
}): ActionFeedback {
  if (input.httpError) {
    return feedbackFromError(
      input.httpError,
      'interview_send_failed',
      'interview_already_sent'
    );
  }
  if (input.skipped) {
    return { level: 'info', key: 'interview_already_sent' };
  }
  if (input.willNotify === false) {
    return { level: 'info', key: 'ai_interview_invitation_not_sent' };
  }
  return { level: 'success', key: 'interview_sent' };
}

export function feedbackForPipelineMove(input: {
  error?: string;
  positionError?: string | null;
  actionsQueued?: number;
  toStageType?: string;
  /** Destination stage willNotify (hasAutomationRule && automationEnabled). */
  willNotify?: boolean;
  /**
   * Destination label for surfaces where the move is not visible (Table).
   * Confirms side-effect-free moves; side-effect toasts still take precedence.
   */
  movedToStageLabel?: string;
}): ActionFeedback | null {
  const effect = input.toStageType
    ? MOVE_SIDE_EFFECT[input.toStageType]
    : undefined;
  if (input.error) {
    return feedbackFromError(
      input.error,
      'invalid_status_move',
      effect?.skipped ?? 'already_in_stage'
    );
  }
  if (input.positionError) {
    return { level: 'warning', key: 'position_save_failed' };
  }
  if (!effect) {
    return input.movedToStageLabel
      ? {
          level: 'success',
          key: 'candidate_moved',
          params: { stage: input.movedToStageLabel },
        }
      : null;
  }
  if (input.actionsQueued == null) {
    return null;
  }
  if (input.actionsQueued > 0) {
    return { level: 'success', key: effect.queued };
  }
  // Automation off / no rule: do not claim "already sent".
  if (input.willNotify === false) {
    return {
      level: 'info',
      key: notSentKeyForStage(input.toStageType),
    };
  }
  return { level: 'info', key: effect.skipped };
}

export function feedbackForStageMutation(input: {
  kind: 'create' | 'rename' | 'reorder' | 'delete' | 'automation';
  error: string;
}): ActionFeedback {
  const lower = input.error.toLowerCase();
  if (lower.includes('default stages cannot be deleted')) {
    return { level: 'error', key: 'stage_delete_default_blocked' };
  }
  if (lower.includes('still assigned')) {
    return { level: 'error', key: 'stage_delete_not_empty' };
  }
  return feedbackFromError(input.error, `stage_${input.kind}_failed`);
}

/** Client-side Kanban gate — same cases as canMoveCandidateToStage. */
export function feedbackForBlockedClientMove(input: {
  fromType?: string | null;
  toType?: string | null;
  fromPosition?: number;
  toPosition?: number;
}): ActionFeedback {
  if (input.toType === 'interviewed') {
    return { level: 'error', key: 'interviewed_stage_system_only' };
  }
  if (input.fromType === 'interviewed' && input.toType === 'interviewing') {
    return { level: 'error', key: 'interviewed_to_interviewing' };
  }
  if (
    input.fromType === 'interviewed' &&
    input.fromPosition != null &&
    input.toPosition != null &&
    input.toPosition < input.fromPosition
  ) {
    return { level: 'error', key: 'interviewed_no_earlier_stage' };
  }
  return { level: 'error', key: 'invalid_status_move' };
}

/** Narrow i18next `t` — do not type this as `TFunction`; its overloads recurse. */
type TranslateKey = (
  key: string,
  options?: Record<string, string | number>
) => string;

export function applyActionFeedback(
  t: unknown,
  i18nPrefix: string,
  feedback: ActionFeedback | null
): void {
  if (!feedback) return;
  const translate = t as TranslateKey;
  const message =
    feedback.text ??
    (feedback.key
      ? translate(`${i18nPrefix}.${feedback.key}`, feedback.params)
      : '');
  if (!message) return;
  toast[feedback.level](message);
}
